"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X, Check } from "lucide-react";
import { cn } from "@sharpr/ui";
import type { Sport } from "@sharpr/types";
import { startAddSportCheckout } from "@/utils/addSport";
import {
  deserializeTerms,
  type SubscriptionTermsDTO,
} from "@/services/billing/subscriptionTerms";
import { getStripeBrowser } from "@/lib/stripe/browser";
import { CONSENT_TERMS_VERSION } from "@/lib/billing/trialConsent";
import { getSportName } from "@/utils/sportName";
import { SubscriptionDisclosure } from "./SubscriptionDisclosure";
import { useModalDialog } from "./useModalDialog";

const CHECKOUT_FALLBACK_ERROR = "Couldn't start checkout. Please try again.";

interface Quote {
  saved_payment_method: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  terms: SubscriptionTermsDTO;
  disclosed_amount_cents: number;
  anchor_ms: number;
}

type Phase =
  | "loading"
  | "ready"
  | "submitting"
  | "success"
  | "declined"
  | "error";

interface AddSportDialogProps {
  sport: Sport;
  onClose: () => void;
  onAdded: () => void;
}

export function AddSportDialog({
  sport,
  onClose,
  onAdded,
}: AddSportDialogProps) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [quote, setQuote] = useState<Quote | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const tokenRef = useRef<string>(globalThis.crypto.randomUUID());
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const { dialogRef } = useModalDialog({
    onClose,
    canClose: phase !== "submitting",
    initialFocusRef: confirmButtonRef,
    fallbackToDialog: true,
  });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch("/api/billing/add-sport-quote", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sport }),
        });
        const json = await res.json().catch(() => ({}));
        if (!active) return;
        if (!res.ok || json.eligible === false) {
          try {
            await startAddSportCheckout(sport);
          } catch {
            if (!active) return;
            setErrorMsg(CHECKOUT_FALLBACK_ERROR);
            setPhase("error");
          }
          return;
        }
        setQuote(json as Quote);
        setPhase("ready");
      } catch {
        if (!active) return;
        try {
          await startAddSportCheckout(sport);
        } catch {
          if (!active) return;
          setErrorMsg(CHECKOUT_FALLBACK_ERROR);
          setPhase("error");
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [sport]);

  const handleConfirm = useCallback(async () => {
    if (!quote || phase === "submitting") return;
    setPhase("submitting");
    const goToCheckout = async () => {
      try {
        await startAddSportCheckout(sport);
      } catch {
        setErrorMsg(CHECKOUT_FALLBACK_ERROR);
        setPhase("error");
      }
    };
    try {
      // Resolve Stripe.js UP FRONT. A paid add needs it to confirm the invoice PI; if the
      // publishable key is unset we must fall back to checkout BEFORE creating a sub —
      // otherwise we'd create an incomplete sub and then bounce → duplicate.
      const stripe = await getStripeBrowser();
      if (!stripe) {
        await goToCheckout();
        return;
      }

      const res = await fetch("/api/billing/add-sport", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sport,
          disclosed_amount_cents: quote.disclosed_amount_cents,
          terms_version: CONSENT_TERMS_VERSION,
          anchor_ms: quote.anchor_ms,
          idempotency_token: tokenRef.current,
        }),
      });
      const json = await res.json().catch(() => ({}));

      if (json.eligible === false) {
        await goToCheckout();
        return;
      }
      if (json.status === "active") {
        setPhase("success");
        onAdded();
        return;
      }
      if (json.status === "requires_confirmation" && json.client_secret) {
        const result = await stripe.confirmCardPayment(json.client_secret);
        if (result.error) {
          setPhase("declined");
          return;
        }
        // No `error` does NOT imply charged — only `succeeded` does. A `processing` /
        // `requires_*` PI must NOT show success (the access row would never land).
        if (result.paymentIntent?.status !== "succeeded") {
          setErrorMsg("Payment didn't complete. Please try again or update your card.");
          setPhase("error");
          return;
        }
        setPhase("success");
        onAdded();
        return;
      }
      setErrorMsg(
        json.error === "disclosure_stale" ||
          json.error === "signup_anchor_expired"
          ? "The price changed — please reopen and try again."
          : (json.error ?? "Something went wrong."),
      );
      setPhase("error");
    } catch {
      setErrorMsg("Network error. Please try again.");
      setPhase("error");
    }
  }, [quote, phase, sport, onAdded]);

  // The dialog charges the saved card; routing to the full checkout lets the user pay with a
  // different card / wallet (or recover an expired one) without leaving the add-sport flow.
  const useDifferentCard = useCallback(async () => {
    if (phase === "submitting") return;
    setPhase("submitting");
    try {
      await startAddSportCheckout(sport);
    } catch {
      setErrorMsg(CHECKOUT_FALLBACK_ERROR);
      setPhase("error");
    }
  }, [phase, sport]);

  if (typeof document === "undefined") return null;

  const sportName = getSportName(sport);

  return createPortal(
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-sport-dialog-title"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white dark:bg-bg-secondary rounded-lg shadow-xl max-w-md w-full p-6 focus:outline-none"
      >
        <div className="flex items-start justify-between mb-4">
          <h2 id="add-sport-dialog-title" className="text-lg font-semibold">
            Add {sportName}
          </h2>
          <button
            onClick={onClose}
            disabled={phase === "submitting"}
            aria-label="Close"
            className="text-muted-foreground hover:text-fg dark:hover:text-text-primary disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {phase === "loading" && (
          <p className="text-sm text-muted-foreground dark:text-text-tertiary py-6 text-center">
            Loading…
          </p>
        )}

        {(phase === "ready" || phase === "submitting") && quote && (
          <>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5 mb-4">
              <span className="text-sm font-medium text-fg dark:text-text-primary capitalize">
                {quote.saved_payment_method.brand} ••••{" "}
                {quote.saved_payment_method.last4}
              </span>
              <span className="text-sm text-muted-foreground dark:text-text-tertiary tabular-nums">
                {String(quote.saved_payment_method.exp_month).padStart(2, "0")}/
                {String(quote.saved_payment_method.exp_year).slice(-2)}
              </span>
            </div>

            <p className="text-sm text-muted-foreground dark:text-text-tertiary leading-relaxed mb-4">
              <SubscriptionDisclosure
                terms={deserializeTerms(quote.terms)}
                firstChargeCents={quote.disclosed_amount_cents}
              />
            </p>

            <div className="flex items-center justify-between gap-2 mt-2">
              <button
                onClick={useDifferentCard}
                disabled={phase === "submitting"}
                className={cn(
                  "text-xs text-muted-foreground hover:text-fg dark:hover:text-text-primary underline underline-offset-2",
                  "disabled:opacity-50 disabled:no-underline",
                )}
              >
                Use a different card
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  disabled={phase === "submitting"}
                  className={cn(
                    "px-4 py-2 text-sm font-medium rounded-md",
                    "text-muted-foreground hover:bg-bg-tertiary",
                    "disabled:opacity-50",
                  )}
                >
                  Cancel
                </button>
                <button
                  ref={confirmButtonRef}
                  onClick={handleConfirm}
                  disabled={phase === "submitting"}
                  className={cn(
                    "px-4 py-2 text-sm font-medium rounded-md",
                    "bg-brand-500 text-white hover:bg-brand-400",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                  )}
                >
                  {phase === "submitting" ? "Adding…" : "Confirm"}
                </button>
              </div>
            </div>
          </>
        )}

        {phase === "success" && (
          <div className="py-4">
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-4">
              <Check className="h-5 w-5" />
              <span className="text-sm font-medium">{sportName} added.</span>
            </div>
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className={cn(
                  "px-4 py-2 text-sm font-medium rounded-md",
                  "bg-brand-500 text-white hover:bg-brand-400",
                )}
              >
                Done
              </button>
            </div>
          </div>
        )}

        {phase === "declined" && (
          <div className="py-2">
            <p
              role="alert"
              className="text-sm text-red-600 dark:text-red-400 mb-4"
            >
              Your saved card was declined. Update your payment method to add{" "}
              {sportName}.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={onClose}
                className={cn(
                  "px-4 py-2 text-sm font-medium rounded-md",
                  "text-muted-foreground hover:bg-bg-tertiary",
                )}
              >
                Close
              </button>
              <a
                href="/settings"
                className={cn(
                  "px-4 py-2 text-sm font-medium rounded-md",
                  "bg-brand-500 text-white hover:bg-brand-400",
                )}
              >
                Update payment
              </a>
            </div>
          </div>
        )}

        {phase === "error" && (
          <div className="py-2">
            <p
              role="alert"
              className="text-sm text-red-600 dark:text-red-400 mb-4"
            >
              {errorMsg}
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={onClose}
                className={cn(
                  "px-4 py-2 text-sm font-medium rounded-md",
                  "text-muted-foreground hover:bg-bg-tertiary",
                )}
              >
                Close
              </button>
              {/* "Try again" only when we have a quote to return to — a mount-fetch
                  fallback error has no quote, so 'ready' would render an empty body. */}
              {quote != null && (
                <button
                  onClick={() => {
                    setErrorMsg(null);
                    setPhase("ready");
                  }}
                  className={cn(
                    "px-4 py-2 text-sm font-medium rounded-md",
                    "bg-brand-500 text-white hover:bg-brand-400",
                  )}
                >
                  Try again
                </button>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>,
    document.body,
  );
}
